/**
 * Shared filter utilities for admin data tables
 * Provides reusable filter operators for common data types
 */

/**
 * Apply text filter with operator support
 * @param value - The row value to check
 * @param filterValue - The filter value to compare against
 * @param operator - The filter operator (contains, is, isNot)
 * @returns true if the row should be included
 */
export const applyTextFilter = (
  value: string | number | null | undefined,
  filterValue: string,
  operator: string
): boolean => {
  const rowValue = String(value || '').toLowerCase();
  const filter = filterValue.toLowerCase();

  switch (operator) {
    case 'contains':
      return rowValue.includes(filter);
    case 'is':
      return rowValue === filter;
    case 'isNot':
      return rowValue !== filter;
    default:
      return true;
  }
};

/**
 * Apply array filter (for fields that contain multiple values)
 * @param values - Array of values to check
 * @param filterValue - The filter value to compare against
 * @param operator - The filter operator (contains, is, isNot)
 * @returns true if the row should be included
 */
export const applyArrayFilter = (
  values: (string | null)[] | undefined,
  filterValue: string,
  operator: string
): boolean => {
  if (!values || values.length === 0) return false;
  
  const filter = filterValue.toLowerCase();
  const safeValues = values.filter((v): v is string => !!v);

  switch (operator) {
    case 'contains':
      return safeValues.some(val => val.toLowerCase().includes(filter));
    case 'is':
      return safeValues.some(val => val.toLowerCase() === filter);
    case 'isNot':
      return !safeValues.some(val => val.toLowerCase() === filter);
    default:
      return true;
  }
};

/**
 * Apply date filter with operator support
 * @param value - The row date value to check
 * @param filterValue - The filter date value (string)
 * @param operator - The filter operator (onOrAfter, onOrBefore)
 * @returns true if the row should be included
 */
export const applyDateFilter = (
  value: string | Date | null | undefined,
  filterValue: string,
  operator: string
): boolean => {
  if (!value) return false;

  const rowDate = new Date(value);
  const filterDate = new Date(filterValue);

  // Invalid dates
  if (isNaN(rowDate.getTime()) || isNaN(filterDate.getTime())) {
    return false;
  }

  switch (operator) {
    case 'onOrAfter':
      return rowDate >= filterDate;
    case 'onOrBefore':
      return rowDate <= filterDate;
    case 'is':
      // Compare dates (ignore time)
      return rowDate.toDateString() === filterDate.toDateString();
    default:
      return true;
  }
};

/**
 * Generic sort comparator for data table rows
 * @param a - First row value
 * @param b - Second row value
 * @param sortDirection - 'asc' or 'desc'
 * @returns comparison result (-1, 0, 1)
 */
export const compareValues = (
  a: any,
  b: any,
  sortDirection: 'asc' | 'desc'
): number => {
  const direction = sortDirection === 'desc' ? -1 : 1;

  // Handle null/undefined
  if (a == null && b == null) return 0;
  if (a == null) return 1 * direction;
  if (b == null) return -1 * direction;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return (a.getTime() - b.getTime()) * direction;
  }

  // Handle date strings
  if (typeof a === 'string' && typeof b === 'string') {
    const aDate = new Date(a);
    const bDate = new Date(b);
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      return (aDate.getTime() - bDate.getTime()) * direction;
    }
    // String comparison
    return a.localeCompare(b) * direction;
  }

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * direction;
  }

  // Handle arrays (join and compare as strings)
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.join(', ').localeCompare(b.join(', ')) * direction;
  }

  // Default string comparison
  return String(a).localeCompare(String(b)) * direction;
};

